import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getPtMissedVisitForm,
  updatePtMissedVisitForm,
} from "../../../../../services/patientService";
import {
  AlertCircleIcon,
  CommentsIcon,
  PhoneIcon,
  TargetIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  PT_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  ATTEMPT_COUNT_OPTIONS,
  CONTACT_METHOD_OPTIONS,
  INTERVENTION_AFFECTED_OPTIONS,
  MISSED_REASON_OPTIONS,
  PT_MISSED_ARRAY_KEYS,
  YES_NO_OPTIONS,
  emptyPtMissedVisitPayload,
  toggleInList,
  today,
} from "./ptMissedVisitContent";
import {
  assertPtVisitMetaForFlag,
  buildPtVisitMetaProps,
  pickPtVisitMeta,
} from "./ptVisitCommon";
import PtNotesCommonHeader from "./PtNotesCommonHeader";
import {
  normalizePtCommonHeaderState,
  resolvePtNotesCommonHeader,
  withNormalizedPtCommonHeader,
} from "./ptNotesCommonHeaderUtils";

const MISSED_VISIT_DATE_MAX = new Date().toISOString().slice(0, 10);

const normalizeExclusiveYesNo = (value) => {
  if (Array.isArray(value)) {
    if (value.includes("Yes")) return "Yes";
    if (value.includes("No")) return "No";
    return "";
  }
  const s = String(value || "").trim();
  if (s === "Yes" || s === "No") return s;
  return "";
};

const normalizeAttemptCount = (value) => {
  const s = String(value || "").trim();
  return ATTEMPT_COUNT_OPTIONS.includes(s) ? s : "";
};

const CONTACT_METHOD_ICONS = {
  "Phone Call": PhoneIcon,
  "Text Message": CommentsIcon,
  Voicemail: PhoneIcon,
};

const emptyValues = () => ({
  ...emptyPtMissedVisitPayload(),
  missedVisitDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const PtMissedVisitForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient?.lastName, patient?.firstName],
  );

  const buildPatientPrefill = useCallback(
    () => ({
      ...emptyPtMissedVisitPayload(),
    }),
    [],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyPtMissedVisitPayload();
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      const merged = {
        ...base,
        ...loadedPayload,
        ...pickPtVisitMeta(loadedPayload, data),
        missedVisitDate:
          data.missedVisitDate || loadedPayload.missedVisitDate || today(),
        commonHeader: normalizePtCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        attemptsCount: normalizeAttemptCount(loadedPayload.attemptsCount),
        awareOfNextVisit: normalizeExclusiveYesNo(
          loadedPayload.awareOfNextVisit,
        ),
        reasonOtherSpecify: String(
          loadedPayload.reasonOtherSpecify ||
            loadedPayload.reasonExplanation ||
            "",
        ).trim(),
        interventionOther: String(loadedPayload.interventionOther || "").trim(),
        planToAddress: String(
          loadedPayload.planToAddress || loadedPayload.functionalImpact || "",
        ).trim(),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      PT_MISSED_ARRAY_KEYS.forEach((key) => {
        merged[key] = Array.isArray(loadedPayload[key])
          ? loadedPayload[key].map(String)
          : Array.isArray(base[key])
            ? base[key]
            : [];
      });

      setValues(merged);
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolvePtNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Physical Therapy Missed Visit",
            formType: clinicalForm.formType || draft?.formType,
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            missedVisitDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: { ...buildPatientPrefill(), commonHeader },
          });
          return;
        }

        const res = await getPtMissedVisitForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolvePtNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: { ...existingPayload, commonHeader },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        PT_FORM_EDITOR_ROLES,
        "View only — only PT/PTA can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      missedVisitDate,
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      ...ptFields
    } = values;

    const nestedPayload = withNormalizedPtCommonHeader({
      ...ptFields,
      missedVisitDate: missedVisitDate
        ? formatDateForInput(missedVisitDate)
        : "",
      attemptsCount: normalizeAttemptCount(values.attemptsCount),
      awareOfNextVisit: normalizeExclusiveYesNo(values.awareOfNextVisit),
      reasonOtherSpecify: String(values.reasonOtherSpecify || "").trim(),
      interventionOther: String(values.interventionOther || "").trim(),
      planToAddress: String(values.planToAddress || "").trim(),
    });

    PT_MISSED_ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    const payload = {
      flag,
      missedVisitDate: missedVisitDate
        ? formatDateForInput(missedVisitDate)
        : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const visitMetaError = assertPtVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updatePtMissedVisitForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.missedVisitDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const showInterventionOther = (values.interventionsAffected || []).includes(
    "Other",
  );

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Physical Therapy Missed Visit
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Physical Therapy | Clinical Documentation System
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          caregiverSlot={caregiverSlot}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          dateLabel="Missed Visit Date"
          dateValue={displayDateUS(values.missedVisitDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Missed Visit Date
                </div>
                <InputDatePicker
                  name="missedVisitDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.missedVisitDate) || ""}
                  onChange={(event) =>
                    setValue("missedVisitDate", event.target.value)
                  }
                  max={MISSED_VISIT_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildPtVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <PtNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        {/* 7 + 8 side-by-side on desktop (matches screenshot) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Section icon={AlertCircleIcon} title="7. Reason for Missed Visit">
            <CheckboxGrid
              options={MISSED_REASON_OPTIONS}
              values={values.missedReasons}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("missedReasons", opt)}
              columns={2}
            />
            <div className="mt-3">
              <Field label="Other (Please Specify)">
                <textarea
                  rows={3}
                  className={textareaClass}
                  value={values.reasonOtherSpecify || ""}
                  disabled={!canEdit}
                  placeholder="Provide additional details..."
                  onChange={(e) =>
                    setValue("reasonOtherSpecify", e.target.value)
                  }
                />
              </Field>
            </div>
          </Section>

          <Section icon={PhoneIcon} title="8. Attempts Made">
            <div>
              <SubHeading>Number of Attempts Made</SubHeading>
              <div className="mt-1.5 grid grid-cols-4 gap-2">
                {ATTEMPT_COUNT_OPTIONS.map((opt) => {
                  const selected = values.attemptsCount === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      disabled={!canEdit}
                      onClick={() =>
                        setValue("attemptsCount", selected ? "" : opt)
                      }
                      className={`h-11 rounded-md border text-sm font-semibold transition-colors ${
                        selected
                          ? "border-indigo-600 bg-indigo-600 text-white"
                          : "border-slate-200 bg-white text-slate-700 hover:border-indigo-300"
                      } ${!canEdit ? "cursor-default opacity-80" : "cursor-pointer"}`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-4">
              <SubHeading>Method(s) of Contact</SubHeading>
              <div className="mt-1.5 flex flex-col gap-2">
                {CONTACT_METHOD_OPTIONS.map((opt) => {
                  const selected = (values.contactMethods || []).includes(opt);
                  const Icon = CONTACT_METHOD_ICONS[opt] || PhoneIcon;
                  return (
                    <button
                      key={opt}
                      type="button"
                      disabled={!canEdit}
                      onClick={() => toggleListValue("contactMethods", opt)}
                      className={`inline-flex items-center gap-2.5 rounded-md border px-3 py-2.5 text-left text-[12px] font-medium transition-colors ${
                        selected
                          ? "border-indigo-500 bg-indigo-50 text-indigo-800"
                          : "border-slate-200 bg-white text-slate-700 hover:border-indigo-300"
                      } ${!canEdit ? "cursor-default opacity-80" : "cursor-pointer"}`}
                    >
                      <Icon size={16} className="shrink-0 opacity-80" />
                      <span>{opt}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </Section>
        </div>

        {/* 9. Impact on Plan of Care */}
        <Section icon={TargetIcon} title="9. Impact on Plan of Care">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="rounded-md border border-indigo-100 bg-indigo-50/60 px-3 py-2.5">
                <SubHeading>
                  Was patient made aware of next scheduled visit?
                </SubHeading>
                <div className="mt-1.5">
                  <ExclusiveChoiceGrid
                    name="awareOfNextVisit"
                    options={YES_NO_OPTIONS}
                    value={values.awareOfNextVisit || ""}
                    canEdit={canEdit}
                    onChange={(opt) => setValue("awareOfNextVisit", opt)}
                    columns={2}
                  />
                </div>
              </div>

              <div>
                <SubHeading>Interventions / Goals Affected</SubHeading>
                <CheckboxGrid
                  options={INTERVENTION_AFFECTED_OPTIONS}
                  values={values.interventionsAffected}
                  canEdit={canEdit}
                  onToggle={(opt) =>
                    toggleListValue("interventionsAffected", opt)
                  }
                  columns={2}
                />
                {showInterventionOther ? (
                  <input
                    type="text"
                    className={`${fieldClass} mt-1.5`}
                    value={values.interventionOther || ""}
                    disabled={!canEdit}
                    placeholder="Specify other intervention…"
                    onChange={(e) =>
                      setValue("interventionOther", e.target.value)
                    }
                  />
                ) : null}
              </div>
            </div>

            <Field label="Plan to Address / Make Up Visit">
              <textarea
                rows={10}
                className={textareaClass}
                value={values.planToAddress || ""}
                disabled={!canEdit}
                placeholder="Specify how missed interventions will be rescheduled or addressed..."
                onChange={(e) => setValue("planToAddress", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection={false}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="PT / PTA Authentication"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview ||
        permissions.canEdit) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

PtMissedVisitForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default PtMissedVisitForm;
